import { faEye, faEyeSlash, faSignIn, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import { Alert, Button, Modal, ModalBody, Spinner } from "reactstrap";
import { ArrowLeft } from "lucide-react";
import * as Yup from "yup";
import getDataFromApi from "../../components/getDataFromApi";
import "./Login.scss";
import { EyeIcon, EyeOffIcon, LoginIcon, RegisterIcon, SingupIcon, VersionsIcon } from "../Common/SvgIcons/WorkflowIcons";
import TermsandConditions from "../../components/TermsandConditions";

/* ---------------------------------------------------
   Cookie + Logout Helpers
--------------------------------------------------- */
export function getCokkie(key) {
  try {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(key))
      .split("=")[1];
  } catch (err) {
    console.log("No user Found");
  }
}

export function getrefreshCokkie(key) {
  try {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(key))
      .split("=")[1];
  } catch (err) {
    console.log("No user Found");
  }
}


/* ---------------------------------------------------
   Tenant id Helper
--------------------------------------------------- */

export function getTenantId() {
  try {
    return localStorage.getItem("tenant_id");
  } catch (err) {
    console.log("Unable to fetch tenant_id", err);
  }
}


export async function logout() {
  try {
    // Drop Develop App terminal session before leaving the app.
    window.dispatchEvent(new Event("deepedge:logout"));

    const response = await getDataFromApi(
      `${window["ENV"].API_URL}/authentication/saml/logout/initiate?tenant_id=${getTenantId()}`,
      "get"
    );

    // Clear local auth data
    document.cookie = `jwtToken=;path=/`;
    localStorage.clear();
    // Login is a light-only surface — don't leave dark theme attrs behind
    document.documentElement.setAttribute("data-theme", "light");

    // Redirect based on response
    if (response?.logout_url) {
      window.location.replace(response.logout_url);
    } else {
      window.location.replace("/login");
    }
  } catch (error) {
    console.error(error);

    // Fallback: local logout
    document.cookie = `jwtToken=;path=/`;
    localStorage.clear();
    document.documentElement.setAttribute("data-theme", "light");
    window.location.replace("/login");
  }
}


/* ---------------------------------------------------
   MAIN LOGIN SCREEN
--------------------------------------------------- */
export default function Login() {
  const [tab, setTab] = React.useState("login");
  const [visibleItems, setVisibleItems] = React.useState([]);

  // Login UI is always light. Prevent rare dark-theme flashes when
  // data-theme="dark" is left on <html> (logout, system preference, etc.).
  React.useLayoutEffect(() => {
    const html = document.documentElement;
    const previousTheme = html.getAttribute("data-theme");
    html.setAttribute("data-theme", "light");
    return () => {
      const saved = localStorage.getItem("theme");
      if (saved === "dark") {
        html.setAttribute("data-theme", "dark");
      } else if (saved === "system") {
        const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        html.setAttribute("data-theme", isDark ? "dark" : "light");
      } else if (saved === "light") {
        html.setAttribute("data-theme", "light");
      } else if (previousTheme) {
        html.setAttribute("data-theme", previousTheme);
      } else {
        html.removeAttribute("data-theme");
      }
    };
  }, []);

  React.useEffect(() => {
    // Runs every 1.5 seconds
    const interval = setInterval(() => {
      const count = Math.floor(Math.random() * 4) + 1; // show 1–4 items

      const shuffled = [0, 1, 2, 3].sort(() => Math.random() - 0.5);
      const randomSubset = shuffled.slice(0, count);

      setVisibleItems(randomSubset);
    }, 1500);

    return () => clearInterval(interval);
  }, []);


  const workflow = [
    {
      title: "Build",
      desc: "Design and create your AI models.",
      icon: (
        <svg className="w-7 h-7 card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M3 7h18M3 12h18M3 17h18M7 3v18" />
        </svg>

      )
    },
    {
      title: "Optimize",
      desc: "Tune models for performance and efficiency.",
      icon: (
        <svg className="w-7 h-7 card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
        </svg>

      )
    },
    {
      title: "Evaluate",
      desc: "Measure model latency, accuracy and robustness.",
      icon: (
        <svg className="w-7 h-7 card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M5 12h2m3-5h2m3 8h2M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>

      )
    },
    {
      title: "Deploy",
      desc: "Push models securely to edge hardware.",
      icon: (
        <svg className="w-7 h-7 card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M12 3v12m0 0l4-4m-4 4l-4-4M5 19h14a2 2 0 002-2v-3H3v3a2 2 0 002 2z" />
        </svg>
      )
    }
  ];


  return (
    <div className="project-d-a de-login-page" style={{ width: "100%", height: "100%" }}>

      {/* LEFT SIDE PANEL */}
      <div className="left-panel h-100  d-a flex-column">

        <div className="d-j-a flex-column h-100 w-100">
          {/* <img
            style={{ maxWidth: "150px" }}
            className="p-2 mt-3 ps-0 de-logo"
            src={window.ENV.LOGO_URL}
            alt="partner_logo"
          /> */}
          <div className="de-logo d-j-a">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="30px"
              viewBox="0 0 31 28"
              fill="none"
            >
              <path
                d="M23 3h-9C7.925 3 3 7.925 3 14v0c0 6.075 4.925 11 11 11h9M21 14h7"
                stroke="#1a73e8"
                strokeWidth="6"
                strokeLinecap="round"
              ></path>
            </svg>{" "}
            &nbsp;  <span className="de-gradient-text">DeepEdge</span> </div>



          <div className=" left-panel-sub">
            <h2 className=" my-0 mb-0 fw-bold" style={{color:'var(--leading-color)'}}>Accelerating AI Innovations</h2>
            <div style={{ fontWeight: '100' }} className=" mb-1 fw-bold pb-4 mt-0">
              {/* The Unified Platform for ML  Development and Deployment */}
              Design in the cloud. Execute at the edge. Scale with confidence.
            </div>
            {/* <h4 style={{ color: "#e0e5f094" }} className="mb-5">
            Development and Deployment
          </h4> */}

            {/* <h6 class="text-lg mt-4 font-semibold mb-4 text-gray-200 uppercase tracking-wider text-white">
              MLOps Workflow
            </h6> */}

            <div className="workflow-list">
              {workflow.map((item, index) => (
                <div
                  key={index}
                  className={`workflow-item ${visibleItems.includes(index) ? "show" : ""}`}
                >
                  {item.icon}
                  <div>
                    <p className="item-title  text-white fw-bold">{item.title}</p>
                    <p className="item-desc text-white">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>


          </div>

          {/* Logo Shape Graphic */}
          <div style={{ position: 'absolute', marginTop: '7.5%' }} >
            <LoginIcon height="200px" className="left-svg" />
          </div>
        </div>

        <p className="w-100 d-j-a left-copy-right text-white">
          {`© DeepEdge ${window.ENV.CUSTOMER === "ambarella" ? "2024" : "2019"
            }-${new Date().getFullYear()}. All rights reserved.`}
        </p>
      </div>

      {/* RIGHT SIDE LOGIN CARD */}
      <div className="de-login-wrapper flex-fill bg-network-login">

        <div className="de-login-card">
          <h2 className="de-login-title">DeepEdge AI Platform</h2>
          <p className="de-login-subtitle">{tab === "login" ? 'Sign in to access your Physical AI development and deployment workspace' : 'Transform how you build and deploy Physical AI. Sign up to get started.'}</p>

          <div className="grey-card1-1 p-1 mb-2 d-a flex-row">
            <button
              className={`rounded1-2 ${tab === "login" ? "de-login-tab de-login-tab--active" : "de-login-tab"} flex-fill d-j-a gap-2`}
              style={{ border: 'none' }}
              onClick={() => setTab("login")}
            >
              <SingupIcon className="wf-icon-small" />
              Sign In
            </button>

            <button
              className={`rounded1-2 ${tab === "signup" ? "de-login-tab de-login-tab--active" : "de-login-tab"} flex-fill d-j-a gap-1`}
              style={{ border: 'none' }}
              onClick={() => setTab("signup")}
            >
              <RegisterIcon className="wf-icon-small" />
              Sign Up
            </button>
          </div>

          {tab === "login" &&
            <button className="de-google-btn" onClick={() => window.location.href = `${window.ENV.API_URL}/authentication/google`}>
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" />
              Continue with Google
            </button>}
          {tab === "login" &&
            <button className="de-google-btn mt-2">
              <img src="https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png" alt="GitHub" />

              Continue with GitHub
            </button>}
          {tab === "login" &&
            <div className="de-divider">
              <span>Or
                {/* continue with */}
              </span>
            </div>}

          <LoginForm tab={tab} setTab={setTab} />

        </div>
        <p className="w-100 right-copy-right mt-3">
          {`© DeepEdge ${window.ENV.CUSTOMER === "ambarella" ? "2024" : "2019"
            }-${new Date().getFullYear()}. All rights reserved.`}
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------------------------
   VALIDATION SCHEMAS
--------------------------------------------------- */
const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  password: Yup.string().required("Required"),
});

const ResetPasswordSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
});

const SignupSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  organization: Yup.string().required("Required"),
  industry: Yup.string().required("Required"),
  purpose: Yup.string(),
});

const PASSWORD_RESET_SUPPORT_EMAIL = "support@deepedge.ai";
const LOGIN_POLICY_DOCUMENTS = {
  terms: {
    pdfUrl: "/terms/terms-and-conditions-in-login-page.pdf",
    title: "Terms of Service",
  },
  privacy: {
    pdfUrl: "/terms/Privacypolicy.pdf",
    title: "Privacy Policy",
  },
};

export function buildPasswordResetMailto(email) {
  const trimmedEmail = email.trim();
  const subject = `Forgotten password request for ${trimmedEmail}`;
  const body = [
    "Hello DeepEdge Support,",
    "",
    `The user with registered email address ${trimmedEmail} has forgotten their password.`,
    "A new password needs to be set/reset for this user.",
    "",
    "Requested from the DeepEdge AI Platform login page.",
  ].join("\n");

  return `mailto:${PASSWORD_RESET_SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
}

function ResetPasswordDialog({ isOpen, initialEmail, onClose }) {
  const [status, setStatus] = React.useState(null);

  React.useEffect(() => {
    if (isOpen) {
      setStatus(null);
    }
  }, [isOpen]);

  const handleSubmit = (values, actions) => {
    window.location.href = buildPasswordResetMailto(values.email);
    // window.open(buildPasswordResetMailto(values.email), "_blank");
    setStatus({
      color: "success",
      message: "A prefilled email has been opened for DeepEdge support.",
    });
    actions.setSubmitting(false);
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      toggle={onClose}
      className="de-reset-modal"
      contentClassName="de-reset-modal-content"
      backdropClassName="de-reset-backdrop"
    >
      <ModalBody className="de-reset-body">
        <div className="de-reset-header">
          <button
            type="button"
            className="de-reset-back-btn"
            onClick={onClose}
            aria-label="Close reset password dialog"
          >
            <ArrowLeft size={20} strokeWidth={2.8} />
          </button>
          <div className="de-reset-title-group">
            <span className="de-reset-eyebrow">Account recovery</span>
            <h2 className="de-reset-title">Reset Password</h2>
          </div>
        </div>

        <p className="de-reset-description">
          Enter the email registered with your DeepEdge account and we will
          prepare a reset request for support.
        </p>

        <Formik
          enableReinitialize
          initialValues={{ email: initialEmail || "" }}
          validationSchema={ResetPasswordSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="de-reset-form">
              <div className="de-reset-input-group">
                <label htmlFor="reset-password-email" className="de-reset-label">
                  Registered email
                </label>
                <Field
                  id="reset-password-email"
                  name="email"
                  type="email"
                  className="de-reset-input"
                  placeholder="name@company.com"
                  aria-label="Reset password email"
                />
                {errors.email && touched.email && (
                  <div className="de-error text-start">{errors.email}</div>
                )}
              </div>

              <button
                type="submit"
                className="de-reset-submit"
                disabled={isSubmitting}
              >
                Submit
              </button>
            </Form>
          )}
        </Formik>

        {status?.message && (
          <Alert color={status.color} className="de-reset-alert">
            {status.message}
          </Alert>
        )}

        <p className="de-reset-note">
          This opens a prefilled email to <strong>{PASSWORD_RESET_SUPPORT_EMAIL}</strong>
          {" "}with the reset details. Send it from your mail app to notify support.
        </p>
      </ModalBody>
    </Modal>
  );
}

/* ---------------------------------------------------
   LOGIN + SIGNUP FORM (FORMIK VERSION)
--------------------------------------------------- */
export function LoginForm({ tab = "login", setTab = () => { } }) {
  const navigate = useNavigate();
  const [show, setShow] = React.useState(false);
  const [alertMsg, setAlertMsg] = React.useState(null);
  const [alertSignup, setAlertSignup] = React.useState(null);
  const [policyDocument, setPolicyDocument] = React.useState(null);
  const [showResetPassword, setShowResetPassword] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState("");

  React.useEffect(() => {
    setAlertMsg(null);
    setAlertSignup(null);
  }, [tab]);

  const handleLogin = async (values, actions) => {
    const urlencoded = new URLSearchParams();
    urlencoded.append("username", values.email);
    urlencoded.append("password", values.password);

    try {
      const response = await fetch(
        `${window.ENV.API_URL}/authentication/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: urlencoded,
          redirect: "follow",
          credentials: "include",
        }
      );

      const result = await response.json();

      if (result?.access_token) {
        document.cookie = `jwtToken=${result.access_token};path=/`;
        document.cookie = `refreshToken=${result.refresh_token};path=/`;
        document.cookie = `expire=${result.expire};path=/`;

        localStorage.setItem("name", result.user_name);
        if (values.email) localStorage.setItem("email", values.email);
        localStorage.setItem("projectId", result.first_project_id);
        localStorage.setItem("navImage", result.user_image);
        localStorage.setItem("solutionRole", result.solution_role);
        localStorage.setItem("role", result.role);
        localStorage.setItem("user_id", result.user_id);
        localStorage.setItem("tenant_id", result.tenant_id);
        localStorage.setItem(
          "privacy_policy_accepted",
          result.privacy_policy_accepted
        );
        localStorage.setItem(
          "privacy_policy_version",
          result.privacy_policy_version
        );
        localStorage.setItem(
          "screen_permissions",
          JSON.stringify(result?.screen_permissions ?? [])
        );
        navigate(0);
      } else {
        setAlertMsg(result?.detail ?? { message: "Internal Server Error" });
        actions.setSubmitting(false);
      }
    } catch (err) {
      setAlertMsg({ message: "Internal Server Error" });
      actions.setSubmitting(false);
    }
  };

  const handleSignup = async (values, actions) => {
    try {
      const response = await fetch(
        `${window.ENV.API_URL}/authentication/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(values),
        }
      );

      const data = await response.json();

      if (response.ok && data?.detail?.status) {
        setAlertSignup({
          message: "Request sent successfully!",
          color: "success",
        });

        setTimeout(() => setTab("login"), 1000);
      } else {
        setAlertSignup({
          message: data?.message || "Signup failed. Try again later.",
          color: "danger",
        });

        actions.setSubmitting(false);
      }
    } catch (err) {
      setAlertSignup({
        message: "Something went wrong. Please try again later.",
        color: "danger",
      });

      actions.setSubmitting(false);
    }
  };

  if (tab === "login") {
    return (
      <>
        {policyDocument && (
          <TermsandConditions
            showAccept={false}
            pdfUrl={policyDocument.pdfUrl}
            title={policyDocument.title}
            onClose={() => setPolicyDocument(null)}
          />
        )}

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={LoginSchema}
          onSubmit={handleLogin}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="de-form">
              <div className="de-form-group">
                <div className="de-input-wrapper">
                  <label htmlFor="login-email" className="de-label">
                    Registered email
                    {errors.email && touched.email && <span className="de-error">*</span>}
                  </label>

                  <Field
                    id="login-email"
                    name="email"
                    type="email"
                    className="de-input"
                    placeholder=" "
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="de-form-group">
                <div className="de-input-wrapper">
                  <div className="d-a justify-content-between me-2 flex-row">
                    <label
                      htmlFor="login-password"
                      className="de-label my-0"
                    >
                      Password
                      {errors.password && touched.password && <span className="de-error">*</span>}
                    </label>

                    <button
                      type="button"
                      className="p-0 border-0 bg-transparent"
                      style={{ marginBottom: "-53px", zIndex: "999" }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShow(!show);
                      }}
                    >
                      {!show ? (
                        <EyeOffIcon
                          style={{
                            width: "17px",
                            height: "17px",
                            color: "black",
                          }}
                        />
                      ) : (
                        <EyeIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            color: "black",
                          }}
                        />
                      )}

                      <span className="visually-hidden">
                        {show ? "Hide password" : "Show password"}
                      </span>
                    </button>
                  </div>

                  <Field
                    id="login-password"
                    name="password"
                    type={show ? "text" : "password"}
                    className="de-input"
                    placeholder=" "
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <div
                className="de-forgot-wrapper mb-2"
                style={{ marginTop: "-17px" }}
              >
                <button
                  type="button"
                  className="de-forgot-btn text-secondary"
                  onClick={() => {
                    setAlertMsg({
                      message: "Please contact your admin to reset your password.",
                      color: "warning",
                    });
                  }}
                >
                  Forgot password?
                </button>
              </div>

              {alertMsg?.message && (
                <Alert className="de-alert" color={alertMsg.color || "danger"}>
                  {alertMsg.message}
                </Alert>
              )}

              <Button
                type="submit"
                color="primary"
                className="de-submit-btn mt-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>

              <span className="d-flex pt-4 de-terms-text">
                By Signing in, you agree to our &nbsp;
                <a
                  href={LOGIN_POLICY_DOCUMENTS.terms.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="de-policy-link"
                  onClick={(event) => {
                    event.preventDefault();
                    setPolicyDocument(LOGIN_POLICY_DOCUMENTS.terms);
                  }}
                >
                  Terms of Service
                </a>
                &nbsp;and&nbsp;
                <a
                  href={LOGIN_POLICY_DOCUMENTS.privacy.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="de-policy-link"
                  onClick={(event) => {
                    event.preventDefault();
                    setPolicyDocument(LOGIN_POLICY_DOCUMENTS.privacy);
                  }}
                >
                  Privacy Policy
                </a>
                .
              </span>
            </Form>
          )}
        </Formik>

        <ResetPasswordDialog
          isOpen={showResetPassword}
          initialEmail={resetEmail}
          onClose={() => setShowResetPassword(false)}
        />
      </>
    );
  }

  return (
    <Formik
      initialValues={{
        name: "",
        email: "",
        organization: "",
        industry: "",
        purpose: "",
      }}
      validationSchema={SignupSchema}
      onSubmit={handleSignup}
    >
      {({ errors, touched, submitCount, isSubmitting }) => {
        const showError = (field) =>
          errors[field] && (touched[field] || submitCount > 0);

        return (
          <Form className="de-form" autoComplete="off">
            {/* Hidden dummy fields to catch browser autofill */}
            <input
              type="text"
              name="fake-username"
              autoComplete="username"
              style={{ display: "none" }}
              tabIndex="-1"
            />
            <input
              type="password"
              name="fake-password"
              autoComplete="current-password"
              style={{ display: "none" }}
              tabIndex="-1"
            />

            {/* Full Name */}
            <div className="de-form-group">
              <div className="de-input-wrapper">
                <label htmlFor="signup-full-name" className="de-label">
                  Full Name{" "}
                  {showError("name") && <span className="de-error">*</span>}
                </label>

                <Field
                  id="signup-full-name"
                  name="name"
                  type="text"
                  className="de-input"
                  placeholder=" "
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Email */}
            <div className="de-form-group">
              <div className="de-input-wrapper">
                <label htmlFor="signup-email" className="de-label">
                  Email{" "}
                  {showError("email") && <span className="de-error">*</span>}
                </label>

                <Field
                  id="signup-email"
                  name="email"
                  type="email"
                  className="de-input"
                  placeholder=" "
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Organization */}
            <div className="de-form-group">
              <div className="de-input-wrapper">
                <label htmlFor="signup-organization" className="de-label">
                  Organization{" "}
                  {showError("organization") && (
                    <span className="de-error">*</span>
                  )}
                </label>

                <Field
                  id="signup-organization"
                  name="organization"
                  type="text"
                  className="de-input"
                  placeholder=" "
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Industry */}
            <div className="de-form-group">
              <div className="de-input-wrapper">
                <label htmlFor="signup-industry" className="de-label">
                  Industry{" "}
                  {showError("industry") && (
                    <span className="de-error">*</span>
                  )}
                </label>

                <Field
                  id="signup-industry"
                  name="industry"
                  type="text"
                  className="de-input"
                  placeholder=" "
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Purpose */}
            <div className="de-form-group">
              <div className="de-input-wrapper">
                <label htmlFor="signup-purpose" className="de-label">
                  Purpose{" "}
                  {showError("purpose") && (
                    <span className="de-error">*</span>
                  )}
                </label>

                <Field
                  as="textarea"
                  id="signup-purpose"
                  name="purpose"
                  className="de-input"
                  placeholder=" "
                  autoComplete="new-password"
                />
              </div>
            </div>

            {alertSignup?.message && (
              <Alert color={alertSignup.color} className="de-alert">
                {alertSignup.message}
              </Alert>
            )}

            <Button
              type="submit"
              color="primary"
              className="de-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
}
